/**
 * Normalized transport error produced by the axios layer.
 *
 * Backend error bodies (verified against the identity AND acquire module code):
 *   - identity domain failure: { "detail": "machine_readable_code" }
 *   - acquire domain failure:  { "code": "stale_config_revision", "current_revision": 7 }
 *   - field validation:        { "email": ["Enter a valid email address."] }  (DRF field errors)
 *   - missing Bearer:          { "detail": "Authentication credentials were not provided." }
 *   - both at once:            { "code": "invalid_general_configuration",
 *                                "closing_at": ["Closing datetime must be after opening…"] }
 *
 * There is NO { message, status, error_code } envelope. `code` is whichever of `detail`/`code`
 * the body carried, `status` is the HTTP status, and `fieldErrors` carries DRF field errors.
 * Extra payload an ACQUIRE error attaches — `current_revision`, readiness `items` — is not
 * promoted to a field here; read it from `body` in the hook that needs it.
 */
export interface FieldErrors {
  [field: string]: string[];
}

export class ApiError extends Error {
  /** HTTP status; 0 means the request never reached the server (network failure). */
  readonly status: number;
  /** The backend `detail` code, when the body carried one; null otherwise. */
  readonly code: string | null;
  /** DRF field-error map for 400 validation failures; null otherwise. */
  readonly fieldErrors: FieldErrors | null;
  /** The raw parsed response body, for callers needing more than the fields above. */
  readonly body: unknown;

  constructor(params: {
    status: number;
    code: string | null;
    fieldErrors: FieldErrors | null;
    body: unknown;
    message?: string;
  }) {
    super(
      params.message ??
        params.code ??
        `Request failed with status ${String(params.status)}`
    );
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code;
    this.fieldErrors = params.fieldErrors;
    this.body = params.body;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
